import * as echarts from 'echarts';
import { chartSeries, themePalette } from '@/lib/theme/tokens';

export const VERTICE_LIGHT = 'vertice-light';
export const VERTICE_DARK = 'vertice-dark';

// Text/chrome colors for hand-built tooltip HTML (custom `formatter` strings) —
// the same dark-glass values buildTheme wires into the registered themes below
// and EChartTooltip's buildTooltip() renders. Import these instead of
// re-hardcoding the hexes per chart.
export const TOOLTIP_TEXT_STRONG = '#f8fafc';
export const TOOLTIP_TEXT_HEADING = '#e2e8f0';
export const TOOLTIP_TEXT_MUTED = '#cbd5e1';
export const TOOLTIP_TEXT_FAINT = '#94a3b8';
export const TOOLTIP_DIVIDER = 'rgba(148, 163, 184, 0.2)';

// Dark-theme series + chrome stay hardcoded for now: the `[data-theme="dark"]` remap
// tunes role tokens but not the categorical `--chart-*` hues yet (dark data-viz tuning
// is deferred), so there is no live dark series to read — these are the dark-mode hues.
const darkPalette = [
  '#34d399', '#10b981', '#fb7185', '#fbbf24',
  '#7aa2f7', '#a78bfa', '#fb923c', '#94a3b8',
];

function buildTheme(opts: {
  palette: string[];
  text: string;
  textMuted: string;
  axis: string;
  grid: string;
  tooltipBg: string;
  tooltipBorder: string;
  tooltipText: string;
  background: string;
}) {
  const { palette, text, textMuted, axis, grid, tooltipBg, tooltipBorder, tooltipText, background } = opts;
  return {
    color: palette,
    backgroundColor: 'transparent',
    textStyle: {
      fontFamily: 'Geist Variable, ui-sans-serif, system-ui, sans-serif',
      color: text,
    },
    title: {
      textStyle: { color: text, fontWeight: 600 },
      subtextStyle: { color: textMuted },
    },
    legend: {
      textStyle: { color: textMuted, fontSize: 12 },
      icon: 'circle',
      itemWidth: 8,
      itemHeight: 8,
    },
    tooltip: {
      backgroundColor: tooltipBg,
      borderColor: tooltipBorder,
      borderWidth: 1,
      textStyle: { color: tooltipText, fontSize: 12 },
      extraCssText: 'border-radius: 8px; box-shadow: 0 8px 24px -8px rgba(0,0,0,0.35); backdrop-filter: blur(8px);',
    },
    grid: {
      left: 8,
      right: 12,
      top: 24,
      bottom: 8,
      containLabel: true,
      borderColor: grid,
    },
    categoryAxis: {
      axisLine: { show: false, lineStyle: { color: axis } },
      axisTick: { show: false },
      axisLabel: { color: textMuted, fontSize: 12, hideOverlap: true },
      splitLine: { show: false, lineStyle: { color: grid } },
    },
    valueAxis: {
      axisLine: { show: false, lineStyle: { color: axis } },
      axisTick: { show: false },
      axisLabel: { color: textMuted, fontSize: 12 },
      splitLine: { show: true, lineStyle: { color: grid, type: 'dashed' } },
    },
    timeAxis: {
      axisLine: { show: false, lineStyle: { color: axis } },
      axisTick: { show: false },
      axisLabel: { color: textMuted, fontSize: 12, hideOverlap: true },
      splitLine: { show: false, lineStyle: { color: grid } },
    },
    logAxis: {
      axisLine: { show: false, lineStyle: { color: axis } },
      axisLabel: { color: textMuted, fontSize: 12 },
      splitLine: { show: true, lineStyle: { color: grid, type: 'dashed' } },
    },
    line: {
      symbolSize: 6,
      lineStyle: { width: 2 },
      smooth: true,
    },
    bar: {
      itemStyle: { borderRadius: [4, 4, 0, 0] },
    },
    candlestick: {
      itemStyle: {
        color: palette[1],         // up
        color0: palette[2],        // down
        borderColor: palette[1],
        borderColor0: palette[2],
        borderWidth: 1,
      },
    },
    pie: {
      itemStyle: { borderColor: background, borderWidth: 2 },
    },
    radar: {
      axisLine: { lineStyle: { color: grid } },
      splitLine: { lineStyle: { color: grid } },
      splitArea: { areaStyle: { color: ['transparent', grid] } },
    },
    sankey: {
      nodeWidth: 16,
      nodeGap: 8,
      lineStyle: { color: 'gradient', curveness: 0.5, opacity: 0.5 },
      label: { color: text, fontSize: 12 },
    },
    treemap: {
      itemStyle: { borderColor: background, borderWidth: 1, gapWidth: 2 },
      breadcrumb: { itemStyle: { color: tooltipBg, textStyle: { color: tooltipText } } },
    },
    sunburst: {
      itemStyle: { borderColor: background, borderWidth: 2 },
      label: { color: text, fontSize: 12 },
    },
    heatmap: {
      itemStyle: { borderColor: background, borderWidth: 1 },
    },
    visualMap: {
      textStyle: { color: textMuted },
    },
    dataZoom: {
      textStyle: { color: textMuted },
      borderColor: grid,
      fillerColor: 'rgba(45, 190, 110, 0.10)',
      handleStyle: { color: palette[0], borderColor: palette[0] },
      moveHandleStyle: { color: palette[0] },
      emphasis: { handleStyle: { borderColor: palette[0] } },
    },
    toolbox: {
      iconStyle: { borderColor: textMuted },
      emphasis: { iconStyle: { borderColor: text } },
    },
  };
}

export function registerVerticeThemes() {
  // Re-registers on every call (no memo) — the light theme snapshots live role tokens via
  // getComputedStyle (themePalette/chartSeries), so it must be refreshed whenever the active
  // theme changes. Otherwise a session that booted in dark (initThemeMode sets `data-theme`
  // before the first chart module loads) and later switched to light would reuse a light theme
  // whose text/axis captured dark (near-white) values — invisible on the white canvas. Callers
  // (module load + EChart's data-theme observer) invoke this after the target theme is applied,
  // so tokens read correct; cheap (a few getComputedStyle reads) and registerTheme overwrites.
  const p = themePalette();

  const lightTheme = buildTheme({
    // The sanctioned categorical series (§01.6): the 10 `--chart-*` hues, in order.
    // Primary/semantic hues are never a data series — auto-cycled charts read this.
    palette: chartSeries(),
    text: p.textPrimary,
    textMuted: p.textSecondary,
    axis: p.border,
    grid: 'rgba(148, 163, 184, 0.18)',
    tooltipBg: 'rgba(15, 23, 42, 0.95)',
    tooltipBorder: 'rgba(63, 63, 70, 0.6)',
    tooltipText: TOOLTIP_TEXT_STRONG,
    background: '#ffffff',
  });

  const darkTheme = buildTheme({
    palette: darkPalette,
    text: '#e2e8f0',
    textMuted: '#cbd5e1',
    axis: '#334155',
    grid: 'rgba(148, 163, 184, 0.12)',
    tooltipBg: 'rgba(15, 23, 42, 0.95)',
    tooltipBorder: 'rgba(148, 163, 184, 0.2)',
    tooltipText: TOOLTIP_TEXT_STRONG,
    background: '#0f172a',
  });

  echarts.registerTheme(VERTICE_LIGHT, lightTheme);
  echarts.registerTheme(VERTICE_DARK, darkTheme);
}

export function resolveThemeName(): string {
  if (typeof document === 'undefined') return VERTICE_LIGHT;
  // The theme toggle sets `data-theme` on <html> (see src/lib/theme/theme-mode.ts).
  return document.documentElement.getAttribute('data-theme') === 'dark'
    ? VERTICE_DARK
    : VERTICE_LIGHT;
}

export { darkPalette };
